import { useMutation, useQuery } from '@tanstack/react-query';
import { remixApi } from '@tectonic/api-client';
import { useEffect, useRef } from 'react';
import { usePageFragment, useSharedLocalState } from '../../../hooks';
import { useFragmentValue } from '../../../hooks/useFragmentValue';
import { useHaloScript } from '../../../hooks/useHaloScript';
import { queryKeys } from '../../../queryKeys';

import type { ElemasonProfileInfoWidget } from '@tectonic/types';

const useProfileInfo = (widget: ElemasonProfileInfoWidget) => {
  const { data } = widget;
  const stateKey = data?.stateKey!;
  const ref = useRef(false);
  const product = useHaloScript(data?.product!);
  const fragment = usePageFragment(data?.fragment);
  const fragmentValue = useFragmentValue(fragment);

  const { data: profileData, isLoading } = useQuery({
    enabled: !ref.current,
    queryKey: queryKeys.profileData(),
    queryFn: async () => remixApi.getProfileData(),
  });

  const { data: productProfileSchema, isLoading: ppsIsLoading } = useQuery({
    enabled: !!product,
    queryKey: queryKeys.productProfileSchema(product?.slug!),
    queryFn: async () => remixApi.getProductProfileSchema(product?.slug!),
  });

  const updateProfileDataMutation = useMutation({
    mutationFn: (updatedData: Record<string, any>) =>
      updatedData &&
      remixApi.updateUserProfileData({ profileData: updatedData }),
  });

  const { sharedState, setSharedState } = useSharedLocalState(stateKey);

  useEffect(() => {
    updateProfileDataMutation.mutate(sharedState as Record<string, any>);
  }, [sharedState]);

  useEffect(() => {
    if (!isLoading && profileData) {
      setSharedState(profileData.profileData);
      ref.current = true;
    }
  }, [profileData, isLoading]);

  return {
    product,
    fragment,
    fragmentValue,
    productProfileSchema,
    isLoading: isLoading || ppsIsLoading,
    profileData: sharedState ?? profileData?.profileData,
  };
};

export { useProfileInfo };
